import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useQuery } from '@tanstack/react-query';

import { ChannelManager } from 'app/typings/channelManagers';
import type { AdminHotelInfoResponse } from 'app/typings/core/admin/hotel/hotelInfo';
import type { Hotel, HotelInfo } from 'app/typings/hotels/Hotel/Hotel';
import { apiRequest } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

export const mapAdminHotelInfoToHotel = (
  response: AdminHotelInfoResponse
): HotelInfo => ({
  id: response.id,
  groupId: response.groupId ?? 0,
  name: response.name,
  stars: response.stars ?? 0,
  address: {
    street: response.address.street ?? '',
    zipCode: response.address.zipCode ?? '',
    city: response.address.city ?? '',
    subCity: response.address.subCity,
    department: response.address.department ?? '',
    region: response.address.region ?? '',
    countryId: response.address.countryId,
    full: [
      response.address.street,
      response.address.zipCode,
      response.address.city,
    ]
      .filter(Boolean)
      .join(', '),
  },
  location: {
    locality: response.location.locality ?? '',
    subLocality: response.location.subLocality,
    coords: {
      lat: response.location.coords?.latitude ?? 0,
      lng: response.location.coords?.longitude ?? 0,
    },
  },
  roomCount: response.roomCount,
  checkInTime: response.checkInTime,
  checkOutTime: response.checkOutTime,
  services: {
    safety: [],
    services: response.services.map((service) => ({
      serviceTypeId: service.serviceTypeId,
      serviceKind: service.serviceKind,
      price: service.price,
      openingHours: service.openingHours,
      priceKind: service.priceKind,
      customData: service.customData,
      id: service.id,
      isDeleted: service.isDeleted,
    })),
  },
  spaces: response.spaces.map((space) => ({
    spaceTypeId: space.spaceTypeId,
    name: space.name,
    floor: space.floor,
    phone: space.phone,
    infos: space.infos,
    openingHours: space.openingHours,
    pictures: space.pictures.map((picture) => ({
      pictureId: picture.path,
      size: picture.size,
      dimensions: picture.dimensions,
      updatedAt: picture.updatedAt,
      name: picture.name ?? '',
      alt: picture.alt,
      credit: picture.credit,
      id: picture.id,
    })),
    id: space.id,
    isDeleted: false,
  })),
  brief: {
    websiteUrl: response.brief.websiteUrl,
    bookingUrl: response.brief.bookingUrl,
    bookingComUrl: response.brief.bookingComUrl ?? '',
    instagramUrl: response.brief.instagramUrl,
    instagramLocationUrl: response.brief.instagramLocationUrl,
    tagIds: response.brief.tagIds,
  },
  pspOnboardingDone: response.pspOnboardingDone,
  payoutEnabled: response.payoutEnabled,
  paymentReady: response.paymentReady,
  channelManager:
    (response.channelManager as ChannelManager) || ChannelManager.NONE,
  channelManagerCredentials: response.channelManagerCredentials,
  supplierId: response.supplierId,
  rateMode: response.rateMode as Hotel['rateMode'],
  subway: response.subway,
  touristTaxId: response.touristTaxId,
  countryId: response.address.countryId,
  parentHotelId: response.parentHotelId,
  managerId: response.managerId,
  billingInterval: response.billingInterval as Hotel['billingInterval'],
  stockScrapperEnabled: response.stockScrapperEnabled,
  issuingActivated: response.issuingActivated,
  hasVccBookings: false,
  isLegacy: response.isLegacy,
  slug: response.slug,
  connectedBarBb: response.connectedBarBb,
});

export function useGetHotelInfo(hotelId?: number) {
  return useQuery({
    queryKey: [CORE_QUERY_KEYS.hotelInfo, { id: hotelId }],
    queryFn: hotelId
      ? () =>
          apiRequest({
            path: ENDPOINTS.getHotelInfo,
            params: { id: hotelId.toString() },
          }).then((response: AdminHotelInfoResponse) =>
            mapAdminHotelInfoToHotel(response)
          )
      : undefined,
    enabled: !!hotelId,
    refetchOnWindowFocus: false,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}
